import { ReplaySubject, Subject } from 'rxjs';

/**
 * Bridge between the fetch epic and the render plugin.
 * The epic calls `.next(resource)` once per successful community site map fetch.
 * The plugin subscribes in `start()` and unsubscribes in `stop()`.
 *
 * ReplaySubject(1) is used so that if the epic resolves before the plugin's
 * start() subscribes (a likely race: the epic fires on mapLibraryFetch while
 * start() runs on mapLibraryLoaded), the last value is still delivered.
 * @type {ReplaySubject<SiteMapResource>}
 */
const siteMapResource$ = new ReplaySubject(1);

/**
 * Tracking bridge: plugins/tracking.plugin.js detects map zoom/pan/map-type
 * interactions and calls `.next()` here with the raw payload — this feature has no analytics
 * knowledge of its own (no segmentTrack/googleAnalytics4Event/getAnalyticsTrackingProperties
 * imports anywhere in it). Whichever page embeds this feature subscribes to this — imported via
 * this feature's index.js, not this file directly — and turns the payload into an actual
 * Segment/GA4 dispatch using the build*Event functions in services/segmentEventBuilder.js and
 * services/googleAnalytics4EventBuilder.js, both of which accept exactly this payload shape.
 *
 * Plain Subject, not ReplaySubject — a tracking consumer that subscribes late has nothing
 * useful to catch up on for a one-off interaction.
 * @type {Subject<{ interactionType: string, inputMethod: string, mapType?: string }>}
 */
const mapInteractionOccurred$ = new Subject();

/**
 * Tracking bridge: emits when the listing panel/drawer is updated to a new view — either by an
 * active lot-status chip change (a pill click, `source: 'lot_status_type'`) or by selecting a lot
 * on the map (`source: 'lot_selection'`). `selectedChip`/`previousChip` are only meaningful for
 * the pill path and left null for the lot path. See mapInteractionOccurred$ above for the general
 * pattern this follows.
 * @type {Subject<{ selectedChip: string|null, previousChip: string|null, resultsCount: number,
 *   cardCount: number, source: 'lot_status_type'|'lot_selection', isMobile: boolean }>}
 */
const panelUpdatedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when the mobile lot-status pill bar is scrolled. No payload.
 * See mapInteractionOccurred$ above for the general pattern this follows.
 * @type {Subject<void>}
 */
const panelScrollOccurred$ = new Subject();

/**
 * Tracking bridge: emits on a mobile drawer interaction — either a vertical drag or the
 * collapsed→open transition (opening is a tap, not a drag, but is reported as the same
 * interaction). Mobile-only (the caller gates on isMobile); no payload. See mapInteractionOccurred$
 * above for the general pattern this follows.
 * @type {Subject<void>}
 */
const drawerDraggedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when a lot is selected on the map.
 * See mapInteractionOccurred$ above for the general pattern this follows.
 * @type {Subject<{ lot: object, planId: number|null, specId: number|null, plansCount: number, isMobile: boolean }>}
 */
const lotSelectedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when the listing panel's card grid (Floor Plans, Quick Move-Ins, Model,
 * Coming Soon, or Sold) is scrolled horizontally. Fires on both desktop and mobile — unlike
 * panelScrollOccurred$ (the lot-status pill bar), which is mobile-only. See mapInteractionOccurred$
 * above for the general pattern this follows.
 * @type {Subject<{ isMobile: boolean }>}
 */
const cardsScrolledOccurred$ = new Subject();

/**
 * Tracking bridge: emits when the Available chip's selected tab changes — either a manual click
 * (handlers/tabInteractionHandlers.js) or any other programmatic change to selectedTab (e.g. an
 * automatic fallback if one is added). This Subject observes Redux state via store$, not a specific
 * action's dispatch site, so it fires for any cause that changes the tab. Never fires for the initial
 * default tab on mount, nor when tabs are torn down entirely (switching to a non-Available chip sets
 * selectedTab to null — see redux/dataSlice.js#chipSelected). See mapInteractionOccurred$ above for
 * the general pattern this follows.
 * @type {Subject<{ selectedTab: string, previousTab: string, resultsCount: number, isMobile: boolean }>}
 */
const tabSelectedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when the "Get Notified When Available" button (Coming Soon panel) is
 * clicked. No payload. See mapInteractionOccurred$ above for the general pattern this follows.
 * @type {Subject<void>}
 */
const notifyWhenAvailableClickedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when the user switches TO the Floor Plan Detail view's or the Housing
 * Unit Detail view's Floor plan configuration tab (constants.js's TAB_FLOOR_PLAN_CONFIG), and
 * again if the active plan/unit changes while that tab is already selected (e.g. picking a
 * different plan card without leaving the tab). This represents actually viewing the floor plan
 * image, not just entering the detail flyout — the view's default tab, Lot & elevation, shows an
 * elevation render instead. DOM-observed via the same `[data-tab-value]` delegation as
 * floorPlanDetailTabSelectedOccurred$ below, which it fires alongside (plus a `store$`
 * subscription for the plan/unit-change case, since that tab selection is local plugin state
 * elsewhere and not something `store$` alone can observe). `planId` is `null` for a spec-backed
 * emission and `specId` is `null` for a plan-backed one — the two are mutually exclusive per
 * emission. Because the Configuration tab is hidden entirely whenever the active plan/unit has no
 * floorplans, this Subject structurally never fires for one that has none — `hasConfigurations`
 * is effectively always `true` here. See mapInteractionOccurred$ above for the general pattern
 * this follows.
 * @type {Subject<{ planId: number|null, specId: number|null, lotId: number|null, hasConfigurations: boolean }>}
 */
const floorPlanViewedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when the user switches between the Floor Plan Detail view's own tabs
 * (Lot & elevation / Floor plan configuration — constants.js's TAB_LOT_AND_ELEVATION /
 * TAB_FLOOR_PLAN_CONFIG). That tab selection is local plugin state, not Redux, so this is
 * DOM-observed via delegation on `[data-tab-value]` within `[data-panel]`, filtered to just these
 * two tab values (the Available chip's Floor Plans/Quick Move-Ins tabs share the same attribute
 * but are already covered by tabSelectedOccurred$ above). See mapInteractionOccurred$ above for
 * the general pattern this follows.
 * @type {Subject<{ tabSelected: string, planId: number, specId: number|null, lotId: number|null }>}
 */
const floorPlanDetailTabSelectedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when the mobile-only "View floor plan" button (`[data-image-expand-button]`,
 * shown on the Floor Plan Configuration tab) is clicked. DOM-observed via delegation on
 * `[data-panel]`, same as floorPlanDetailTabSelectedOccurred$ above.
 * @type {Subject<{ planId: number, specId: number|null, lotId: number|null }>}
 */
const viewFloorPlanClickedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when the Floor Plan Configuration tab's "No configurations available"
 * empty state is shown — today that's every time the tab is selected, since
 * components/floorPlanConfiguration.dom.js is still placeholder-only content (see its own doc
 * comment). Fires alongside floorPlanDetailTabSelectedOccurred$ from the same tab-click handler,
 * the same way lotSelectedOccurred$ and panelUpdatedOccurred$ both fire from one lot selection.
 * @type {Subject<{ planId: number, specId: number|null, lotId: number|null }>}
 */
const emptyConfigViewedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when the "Similar homes available" accordion (under a Sold Home Card)
 * is expanded or collapsed. Desktop only — the caller (plugins/tracking.plugin.js) gates on
 * isMobile before calling .next(), since the toggle markup exists on both breakpoints
 * (components/similarHomesSection.dom.js) but only desktop is tracked per the source spec.
 * `expanded` reflects the state AFTER the toggle. See mapInteractionOccurred$ above for the
 * general pattern this follows.
 * @type {Subject<{ expanded: boolean, lotId: number, similarHomesCount: number }>}
 */
const similarHomesToggledOccurred$ = new Subject();

/**
 * Tracking bridge: emits when a similar-home card (under a Sold Home Card's "Similar homes
 * available" list) is clicked. Fires on both desktop and mobile. `planId`/`specId` are mutually
 * exclusive — a candidate is either a floor plan or a housing unit (see
 * components/similarHomesSection.dom.js#renderSimilarHomeCard), never both. `position` is the
 * card's 1-based index within the rendered similar-homes list. See mapInteractionOccurred$ above
 * for the general pattern this follows.
 * @type {Subject<{ lotId: number, planId: number|null, specId: number|null, position: number }>}
 */
const similarHomeCardClickedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when the mobile-only Sold list "See All" button
 * (components/seeAllButton.dom.js, [data-sold-see-all-button]) is clicked. No payload. See
 * mapInteractionOccurred$ above for the general pattern this follows.
 * @type {Subject<void>}
 */
const soldSeeAllClickedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when a user enters a detail view (floor plan or housing unit).
 * Fires only on the transition into the view. `planId` is present for floor plan detail views,
 * `specId` for housing unit (QMI/Model) detail views — never both. `position` indicates how the
 * user arrived: 'card_click' if they clicked a floor plan or housing unit card, 'map_lot' if
 * they clicked a lot polygon on the map, or 'other' if they clicked a Similar Home card under a
 * Sold Home Card (NHS20-8827). See mapInteractionOccurred$ above for the general pattern this
 * follows.
 * @type {Subject<{ lot_id: number, plan_id: number|null, spec_id: number|null,
 *   position: 'card_click'|'map_lot'|'other', isMobile: boolean }>}
 */
const detailViewOpenedOccurred$ = new Subject();

/**
 * Tracking bridge: raw tab click events emitted from tabs.dom.js when a detail view tab is clicked.
 * Fires on both desktop and mobile for both floor plan and housing unit detail views.
 * tracking.plugin subscribes and enriches with context before emitting to detailsTabSelectedOccurred$.
 * @type {Subject<{ tabValue: 'lotAndElevation'|'floorPlanConfiguration' }>}
 */
const detailTabClicked$ = new Subject();

/**
 * Tracking bridge: emits when a detail view tab is switched (Lot & Elevation ↔ Floor Plan Configuration).
 * Fires on both desktop and mobile for both floor plan and housing unit detail views.
 * @type {Subject<{ lotId: number|null, planId: number|null, specId: number|null,
 *   selectedTab: 'lotAndElevation'|'floorPlanConfiguration', isMobile: boolean }>}
 */
const detailsTabSelectedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when a user clicks the "Change Lot" button in detail view.
 * Mobile-only event that navigates to the lot selection view.
 * @type {Subject<{ lotId: number|null, planId: number|null, specId: number|null, isMobile: boolean }>}
 */
const changeLotSelectedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when a user selects a different exterior elevation card.
 * @type {Subject<{ lotId: number|null, planId: number|null, specId: number|null,
 *   elevationId: string|null, elevationTitle: string|null, position: number|null,
 *   isDefault: boolean, isMobile: boolean }>}
 */
const exteriorSelectedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when a user expands the exterior/elevation section.
 * @type {Subject<{ lotId: number|null, planId: number|null, specId: number|null,
 *   availableExteriorsCount: number, isMobile: boolean }>}
 */
const exteriorSelectorExpandedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when a user clicks a lot on the map while browsing in mobile lot card view.
 * @type {Subject<{ lot: object, planId: number|null, specId: number|null, plansCount: number, isMobile: boolean }>}
 */
const mobileLotMapClickedOccurred$ = new Subject();

/**
 * Tracking bridge: emits when a mobile lot card or back button is clicked (native_map_lot_selected_m event).
 * Mobile-only event with full tracking context (lot, plan, spec IDs and optional lot details).
 * Fired via fromEvent listeners in tracking.plugin.js for both lot card ('card_click') and back
 * button ('back_button') clicks.
 * @type {Subject<{ lotId: number|null, planId: number|null, specId: number|null,
 *   lotNumber: string|null, lotGroup: string|null, lotStatus: string,
 *   type: string|null, size: string|null, facing: string|null, clickSource: string, isMobile: boolean }>}
 */
const mobileLotCardNavigationOccurred$ = new Subject();

export default siteMapResource$;
export {
  mapInteractionOccurred$,
  panelUpdatedOccurred$,
  panelScrollOccurred$,
  drawerDraggedOccurred$,
  lotSelectedOccurred$,
  cardsScrolledOccurred$,
  tabSelectedOccurred$,
  notifyWhenAvailableClickedOccurred$,
  floorPlanViewedOccurred$,
  floorPlanDetailTabSelectedOccurred$,
  viewFloorPlanClickedOccurred$,
  emptyConfigViewedOccurred$,
  similarHomesToggledOccurred$,
  similarHomeCardClickedOccurred$,
  soldSeeAllClickedOccurred$,
  detailViewOpenedOccurred$,
  detailTabClicked$,
  detailsTabSelectedOccurred$,
  changeLotSelectedOccurred$,
  exteriorSelectedOccurred$,
  exteriorSelectorExpandedOccurred$,
  mobileLotMapClickedOccurred$,
  mobileLotCardNavigationOccurred$,
};